import React, { useContext, useState } from "react";
import { motion } from "framer-motion";
import phone from "../../assets/svgs/phoneicon.svg";
import email from "../../assets/svgs/mailicon.svg";
import location from "../../assets/svgs/locationicon.svg";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import PrimaryButton from "../common/PrimaryButton";
import FormikField from "../common/FormikField";
import FormikErrorMessage from "../common/FormikErrorMessage";
import EditorTextArea from "../common/EditorTextArea";
import { ContactFormValues, ServiceType } from "../../types/types";
import { sendContactPageData } from "../../service/service";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

// Updated schema to include the checkbox
const SubscribeSchema = Yup.object<ContactFormValues>({
  firstName: Yup.string().required("First Name is required"),
  lastName: Yup.string().required("Last Name is required"),
  organization: Yup.string().required("Organization Name is required"),
  phoneNumber: Yup.string().required("Phone Number is required"),
  message: Yup.string().required("message is required"),
  service: Yup.string().required("Service is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  consentChecked: Yup.boolean()
    .required("Consent is required")
    .oneOf([true], "You must agree to the terms"),
});

type Service = {
  name: string;
  active: boolean;
  serviceType: ServiceType;
};

const services: Service[] = [
  {
    name: "Branding & Marketing",
    active: true,
    serviceType: "Branding & Marketing",
  },
  {
    name: "IT Service",
    active: false,
    serviceType: "It Service",
  },
  {
    name: "Designing",
    active: false,
    serviceType: "Designing",
  },
  {
    name: "Content Writing & Copywriting",
    active: false,
    serviceType: "Content Writing & Copywriting",
  },
  {
    name: "Motion Graphics & Video Editing",
    active: false,
    serviceType: "Motion Graphic & Video Editing",
  },
];

// Updated initial values to include the checkbox
const initialVal: ContactFormValues = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  organization: "",
  message: "",
  email: "",
  service: "Branding & Marketing",
  consentChecked: false,
};

// Removed custom FormikCheckbox component as we're using inline checkbox instead

const NewContactForm = () => {
  const [service, setService] = useState<ServiceType>("Branding & Marketing");
  const [servicesList, setServices] = useState<Service[]>(services);
  const dataContext = useContext(AuthContext);
  const navigate = useNavigate();

  const setActive = (btnName: string) => {
    const newbtn = servicesList.map((services) => {
      services.active = false;
      if (services.name === btnName || services.serviceType === btnName) {
        setService(services.serviceType);
        services.active = true;
      }
      return services;
    });

    setServices(newbtn);
  };

  const handleSubmit = async (
    values: ContactFormValues,
    setSubmitting: (isSubmitting: boolean) => void,
    resetForm: () => void
  ) => {
    try {
      setSubmitting(true);
      const res = await sendContactPageData({
        ...values,
        from: "contact",
      });

      if (!res.status) {
        toast.error("Failed to subscribe, please try again later.");
        return;
      }

      const resData = res.response?.data;
      if (resData.status) {
        toast.success(resData.message);
        // Reset form values after successful submission
        resetForm();
        // Reset service state to default
        setService("Branding & Marketing");
        setActive("Branding & Marketing");
      } else {
        toast.error(resData.message);
      }
    } catch (error) {
      setSubmitting(false);
      toast.error("Failed to subscribe, please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!dataContext) return null;
  const { theme } = dataContext;

  return (
    <div className="w-full mx-auto py-6 sm:py-20 px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row justify-between items-center">
      <div className="lg:w-[45%] mb-8 sm:mb-0">
        <motion.h1
          initial={{ x: -200, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5, type: "spring", stiffness: 200 }}
          className="text-3xl sm:text-4xl xl:text-5xl bricolage-grotesque-bold mb-4 sm:mb-6 text-center sm:text-left"
        >
          Which Service Would You Like to Talk About
        </motion.h1>
        <ul className=" flex gap-4 flex-wrap list-none mb-8">
          {servicesList.map((item, index) => (
            <div key={index} className="flex cursor-pointer">
              <div
                className={`${
                  theme === "dark"
                } p-[1px] custom-gradient2 rounded-full self-start`}
              >
                <li
                  key={index}
                  onClick={() => {
                    setService(item.serviceType);
                    setActive(item.name);
                  }}
                  className={`text-lg ${
                    theme === "dark"
                      ? `${
                          item.active ? "relative !bg-secondary text-white" : ""
                        } bg-white text-black`
                      : `${
                          item.active ? "relative !bg-secondary text-white" : ""
                        } bg-[#141414]`
                  } font-bold rounded-full px-4 py-2`}
                >
                  {item.name}
                </li>
              </div>
            </div>
          ))}
        </ul>
        <div className="grid lg:grid-cols-2 gap-5 lg:gap-8">
          <div className=" flex flex-col items-center sm:items-start font-gtAmerica">
            <div className="flex flex-col sm:flex-row gap-2 justify-center items-center sm:mb-2">
              <div className="size-9 flex justify-center items-center rounded-full bg-secondary">
                <img src={location} alt="" className="" />
              </div>
              <p className="font-montserrat font-medium text-xl">Address</p>
            </div>
            <a
              href="https://www.google.com/maps/dir//86,+Block+A,+Sector+4,+Noida,+Uttar+Pradesh+201301/@28.5837221,77.2386856,12z/data=!4m8!4m7!1m0!1m5!1m1!1s0x390ce50002bb970b:0x7feb4a3b06765e30!2m2!1d77.321087!2d28.583747?entry=ttu&g_ep=EgoyMDI1MDExNC4wIKXMDSoASAFQAw%3D%3D"
              className="font-gtAmerica opacity-70 text-lg text-center sm:text-left"
            >
              A-86, G-01, Sector 4, Noida,
              <br /> UP, 201301
            </a>
          </div>
          <div className=" flex flex-col items-center sm:items-start">
            <div className="flex flex-col sm:flex-row gap-2 justify-center items-center sm:mb-2">
              <div className="size-9 flex justify-center items-center rounded-full bg-secondary">
                <img src={email} alt="" className="" />
              </div>
              <p className="font-montserrat font-medium text-xl">Email</p>
            </div>
            <a
              href="mailto:hello@techqilla.com"
              className="font-gtAmerica opacity-70 text-lg relative z-10"
            >
              hello@techqilla.com
            </a>
          </div>
          <div className=" flex flex-col items-center sm:items-start">
            <div className="flex flex-col sm:flex-row gap-2 justify-center items-center sm:mb-2">
              <div className="size-9 flex justify-center items-center rounded-full bg-secondary">
                <img src={phone} alt="" className="" />
              </div>
              <p className="font-montserrat font-medium text-xl">Phone</p>
            </div>
            <a
              href="tel:+15093315518"
              className="font-gtAmerica opacity-70 text-lg relative z-10"
            >
              +1 (509) 331-5518
            </a>
            <a
              href="tel:+919118677977"
              className="font-gtAmerica opacity-70 text-lg relative z-10"
            >
              +91-9118677977
            </a>
          </div>
        </div>
      </div>
      <div className="lg:w-[50%] p-[1px] bg-white rounded-3xl w-full mb-10 sm:mb-0">
        <div
          className={`${
            theme === "dark" ? "bg-white" : "bg-[#141414] text-white"
          } am:bg-bgHoli bg-no-repeat bg-center transition-all duration-500 flex gap-10 lg:mb-0 px-4 sm:px-10 py-8 sm:py-10 rounded-3xl w-full relative overflow-hidden`}
        >
          <Formik
            initialValues={initialVal}
            enableReinitialize
            validationSchema={SubscribeSchema}
            onSubmit={(values, { setSubmitting, resetForm }) => {
              handleSubmit(values, setSubmitting, resetForm);
            }}
          >
            {({ isSubmitting, setFieldValue, values }) => (
              <Form className="flex flex-col justify-center w-full">
                {isSubmitting ? (
                  <div className="text-center">Submitting...</div>
                ) : (
                  <>
                    <div className="mb-8 w-full lg:grid lg:grid-cols-2 gap-4">
                      <div className="w-full relative z-10">
                        <FormikField
                          type="text"
                          name="firstName"
                          id="firstName"
                          placeholder="Enter Your First Name"
                          className="border-b"
                          required
                        />
                        <FormikErrorMessage name="firstName" />
                      </div>
                      <div className="w-full relative z-10">
                        <FormikField
                          type="text"
                          name="lastName"
                          id="lastName"
                          placeholder="Enter Your Last Name"
                          required
                        />
                        <FormikErrorMessage name="lastName" />
                      </div>

                      <div className="w-full relative z-10">
                        <FormikField
                          type="text"
                          name="email"
                          id="email"
                          placeholder="Enter Your Email"
                          required
                        />
                        <FormikErrorMessage name="email" />
                      </div>
                      <div className="w-full relative z-10">
                        <FormikField
                          type="text"
                          name="organization"
                          required
                          id="organization"
                          placeholder="Enter Your Organization Name"
                        />
                        <FormikErrorMessage name="organization" />
                      </div>
                      <div className="w-full relative z-10">
                        <FormikField
                          type="text"
                          name="phoneNumber"
                          required
                          id="phoneNumber"
                          placeholder="Enter Your Phone Number"
                        />
                        <FormikErrorMessage name="phoneNumber" />
                      </div>

                      <div className="w-full relative z-10">
                        <select
                          name="service"
                          id="service"
                          className={`${
                            theme === "dark"
                              ? "bg-white text-black"
                              : "bg-transparent text-white border-white/30"
                          } transition-all duration-500 w-full px-4 py-3 border-b font-montserrat active:ring-0 focus:ring-0 focus:ring-blue-500 opacity-50`}
                          onChange={(e) => {
                            setFieldValue("service", e.target.value);
                            setService(e.target.value as ServiceType);
                            setActive(e.target.value);
                          }}
                          value={service}
                        >
                          <option
                            value="Branding & Marketing"
                            className={`text-black`}
                          >
                            Branding & Marketing
                          </option>
                          <option value="It Service" className="text-black">
                            It Service
                          </option>
                          <option value="Designing" className="text-black">
                            Designing
                          </option>

                          <option
                            value="Content Writing & Copywriting"
                            className="text-black"
                          >
                            Content Writing & Copywriting
                          </option>
                          <option
                            value="Motion Graphic & Video Editing"
                            className="text-black"
                          >
                            Motion Graphics & Video Editing
                          </option>
                        </select>
                        <FormikErrorMessage name="service" />
                      </div>
                      <div className="col-span-2 relative z-10">
                        <EditorTextArea
                          placeholder="Type Your Message Here"
                          name="message"
                          required
                        />
                        <FormikErrorMessage name="message" />
                      </div>

                      {/* Added checkbox component here */}
                      <div className="col-span-2 mb-4 relative z-10">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="consentChecked"
                            name="consentChecked"
                            checked={values.consentChecked}
                            onChange={(e) => {
                              setFieldValue("consentChecked", e.target.checked);
                            }}
                            className={`w-4 h-4 ${
                              theme === "dark"
                                ? "text-secondary bg-gray-100 border-gray-300"
                                : "bg-transparent border-white/50 text-secondary"
                            } rounded focus:ring-secondary focus:ring-1`}
                          />
                          <label
                            htmlFor="consentChecked"
                            className={`ml-2 text-sm font-medium font-montserrat ${
                              theme === "dark" ? "text-black" : "text-white"
                            } opacity-70`}
                          >
                            I accept the{" "}
                            <span className="underline cursor-pointer" onClick={() => {
                                navigate("/terms-of-services");
                                window.scrollTo({ top: 0, behavior: "smooth" });
                              }}>
                              Terms of Service
                            </span>{" "}
                            &{" "}
                            <span
                              className="underline cursor-pointer"
                              onClick={() => {
                                navigate("/privacy-policy");
                                window.scrollTo({ top: 0, behavior: "smooth" });
                              }}
                            >
                              Privacy Policy
                            </span>
                          </label>
                        </div>
                        <FormikErrorMessage name="consentChecked" />
                      </div>
                    </div>
                    <div className="p-[1px] card-gradient rounded-lg w-full">
                      <PrimaryButton
                        type="submit"
                        disabled={isSubmitting}
                        className="relative bg-white border border-opacity-50 !text-black justify-center z-10 w-full"
                      >
                        Submit
                      </PrimaryButton>
                    </div>
                  </>
                )}
              </Form>
            )}
          </Formik>
          {theme === "dark" ? null : (
            <svg
              width="558"
              height="583"
              viewBox="0 0 558 583"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="absolute top-0 right-0"
            >
              <g filter="url(#filter0_f_3783_3540)">
                <ellipse
                  cx="476.816"
                  cy="93.552"
                  rx="212.4"
                  ry="225.033"
                  fill="#8D19DA"
                />
              </g>
              <defs>
                <filter
                  id="filter0_f_3783_3540"
                  x="0.416016"
                  y="-395.48"
                  width="952.801"
                  height="978.066"
                  filterUnits="userSpaceOnUse"
                  color-interpolation-filters="sRGB"
                >
                  <feFlood flood-opacity="0" result="BackgroundImageFix" />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="BackgroundImageFix"
                    result="shape"
                  />
                  <feGaussianBlur
                    stdDeviation="132"
                    result="effect1_foregroundBlur_3783_3540"
                  />
                </filter>
              </defs>
            </svg>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewContactForm;
